import { SumMoney } from 'model/Money'
import { BomItemType } from 'model/Project/BoMItemRow'
import {
  FinancialDto,
  MoneyDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState } from 'store/configureStore'
import { BomItemPointer } from '../../../../model/Project/BomItemPointer'
import { bomItemSelector } from './bomItemSelector'

export const bomItemTargetPriceSelector =
  (bomItemPointer: BomItemPointer, priceScope?: 'perUnit' | 'total') =>
  (state: RootAppState): MoneyDto | null => {
    const targetPrice = bomItemFinancialSelector(
      bomItemPointer,
      'targetSalesPricePerItem',
    )(state)

    if (!targetPrice) return null

    if (priceScope === 'total') {
      const totalProjectQuantity = bomItemFinancialSelector(
        bomItemPointer,
        'totalProjectQuantity',
      )(state)

      if (typeof totalProjectQuantity !== 'number') return targetPrice // fallback

      return {
        ...targetPrice,
        value: targetPrice.value * totalProjectQuantity,
      }
    }

    return targetPrice
  }

// Utility type guard for MoneyDto (duck typing)
const isMoney = (val: unknown): val is MoneyDto =>
  !!val &&
  typeof val === 'object' &&
  'currency' in (val as any) &&
  'value' in (val as any)

export const bomItemFinancialSum =
  <K extends keyof FinancialDto>(
    bomItemPointers: BomItemPointer[],
    financialProp: K,
  ) =>
  (state: RootAppState): FinancialDto[K] | null => {
    if (!state.project.activeProject?.id) return null

    return bomItemPointers.reduce<FinancialDto[K] | null>((acc, ptr) => {
      const val = bomItemFinancialSelector(ptr, financialProp)(state)
      if (val == null) return acc
      if (acc == null) return val
      if (typeof acc === 'number' && typeof val === 'number') {
        return (acc + val) as FinancialDto[K]
      }
      if (isMoney(acc) && isMoney(val)) {
        return SumMoney([acc, val]) as FinancialDto[K]
      }
      // Type mismatch or unsupported aggregate type
      throw new Error(
        `Cannot aggregate financial property ${String(financialProp)} of differing or unsupported types.`,
      )
    }, null)
  }

export const bomItemFinancialSelector =
  <K extends keyof FinancialDto>(
    bomItemPointer: BomItemPointer,
    financialProp: K,
  ) =>
  (state: RootAppState): FinancialDto[K] | null => {
    if (!state.project.activeProject?.id) return null
    if (!bomItemPointer?.id) return null
    if (bomItemPointer.id === state.project.activeProject.id) {
      return state.project.activeProject.financial[financialProp]
    }

    const bomItem = bomItemSelector(state, bomItemPointer)
    if (!bomItem) return null

    switch (bomItem.type) {
      case BomItemType.assemblyType:
      case BomItemType.assemblyInstance:
      case BomItemType.partType:
      case BomItemType.partInstance:
        return bomItem.financial[financialProp]
      default:
        return null
    }
  }

export const bomItemMaterialFinancialSelector =
  <K extends keyof FinancialDto>(
    bomItemPointer: BomItemPointer,
    activityId: string,
    financialProp: K,
  ) =>
  (state: RootAppState): FinancialDto[K] | null => {
    if (!state.project.activeProject?.id) return null
    if (!activityId) return null
    if (!bomItemPointer) return null
    if (bomItemPointer.id === state.project.activeProject.id) {
      return state.project.activeProject.financial[financialProp]
    }

    const bomItem = bomItemSelector(state, bomItemPointer)
    if (!bomItem) return null

    switch (bomItem.type) {
      case BomItemType.partType: {
        const fin = bomItem.rawMaterialSummary?.financial as
          | Partial<FinancialDto>
          | undefined
        return (fin ? (fin[financialProp] as FinancialDto[K]) : null) ?? null
      }
      default:
        return null
    }
  }
